import React, { useState } from 'react';
import {
  Box,
  Button,
  Card,
  CardBody,
  Container,
  Divider,
  FormControl,
  FormLabel,
  Input,
  InputGroup,
  InputRightElement,
  IconButton,
  VStack,
  HStack,
  Text,
  Link,
  useToast,
  PinInput,
  PinInputField,
  Tabs,
  TabList,
  TabPanels,
  Tab,
  TabPanel,
  Flex,
  Icon,
  Heading,
  SimpleGrid,
} from '@chakra-ui/react';
import { ViewIcon, ViewOffIcon } from '@chakra-ui/icons';
import { FaGoogle, FaApple, FaGithub, FaEnvelope, FaLock } from 'react-icons/fa';
import { Link as RouterLink, useNavigate } from 'react-router-dom';
import { useThemeContext } from '@/contexts/ThemeContext';
import { ThemeToggle } from '@/components/common/ThemeToggle';
import { useApi } from '@/contexts/ApiContext';
import { useAuth } from '@/contexts/AuthContext';

export const LoginPage: React.FC = () => {
  const [email, setEmail] = useState<string>('');
  const [password, setPassword] = useState<string>('');
  const [code, setCode] = useState<string>('');
  const [showPassword, setShowPassword] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [isCodeSent, setIsCodeSent] = useState(false);
  const [activeTab, setActiveTab] = useState(0);
  
  const { login } = useAuth();
  const { colorMode, toggleColorMode } = useThemeContext();
  const toast = useToast();
  const navigate = useNavigate();
  const api = useApi();

  const isDark = colorMode === 'dark';

  const handlePasswordLogin = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!email || !password) return;

    setIsLoading(true);
    try {
      const res = await api.UserApi.signIn({ email: email || '', password: password || '' });
      login(res.data.access_token || '', res.data.refresh_token || '');
      toast({
        title: 'Welcome back!',
        description: 'You have been successfully logged in.',
        status: 'success',
        duration: 3000,
        isClosable: true,
      });
      navigate('/profile');
    } catch (error: any) {
      toast({
        title: 'Login failed',
        description: error?.response?.data?.message || error.message || 'Please check your credentials and try again.',
        status: 'error',
        duration: 5000,
        isClosable: true,
      });
    } finally {
      setIsLoading(false);
    }
  };

  const handleSendCode = async () => {
    if (!email) return;
    setIsLoading(true);
    try {
      await api.AuthApi.sendOneTimeCode({ email: email || '' });
      setIsCodeSent(true);
      toast({
        title: 'Code sent!',
        description: 'Check your email for the verification code.',
        status: 'success',
        duration: 3000,
        isClosable: true,
      });
    } catch (error: any) {
      toast({
        title: 'Failed to send code',
        description: error?.response?.data?.message || error.message || 'Please try again.',
        status: 'error',
        duration: 5000,
        isClosable: true,
      });
    } finally {
      setIsLoading(false);
    }
  };

  const handleCodeLogin = async () => {
    if (!email || !code) return;
    setIsLoading(true);
    try {
      const res = await api.AuthApi.signInWithCode({ email: email || '', code: code || '' });
      login(res.data.access_token || '', res.data.refresh_token || '');
      toast({
        title: 'Welcome!',
        description: 'You have been successfully logged in.',
        status: 'success',
        duration: 3000,
        isClosable: true,
      });
      navigate('/profile');
    } catch (error: any) {
      toast({
        title: 'Login failed',
        description: error?.response?.data?.message || error.message || 'Please check your code and try again.',
        status: 'error',
        duration: 5000,
        isClosable: true,
      });
    } finally {
      setIsLoading(false);
    }
  };

  const handleOAuthLogin = (provider: string) => {
    // TODO: Implement OAuth login
    toast({
      title: 'Coming soon!',
      description: `${provider} login will be available soon.`,
      status: 'info',
      duration: 3000,
      isClosable: true,
    });
  };

  return (
    <Box 
      minH="100vh" 
      bg={isDark ? 'gray.900' : 'gray.50'}
      bgGradient={isDark 
        ? 'linear(to-br, gray.900, blue.900, purple.900)' 
        : 'linear(to-br, blue.50, purple.50, pink.50)'
      }
      py={8}
      position="relative"
    >
      {/* Theme Toggle */}
      <Box position="absolute" top={4} right={4}>
        <ThemeToggle size="md" variant="outline" />
      </Box>
      
      <Container maxW="md" centerContent>
        <VStack spacing={8} w="full">
          {/* Logo/Brand */}
          <VStack spacing={2}>
            <Heading 
              size="xl" 
              bgGradient="linear(to-r, blue.400, purple.500, pink.500)"
              bgClip="text"
              fontWeight="bold"
            >
              Casino Pro
            </Heading>
            <Text color={isDark ? 'gray.400' : 'gray.600'} fontSize="lg">
              Welcome back! Please sign in to continue.
            </Text>
          </VStack>

          <Card 
            w="full" 
            bg={isDark ? 'gray.800' : 'white'}
            shadow="xl" 
            borderRadius="2xl"
            border="1px"
            borderColor={isDark ? 'gray.600' : 'gray.200'}
          >
            <CardBody p={8}>
              <VStack spacing={6}>
                {/* OAuth Providers */}
                <VStack spacing={4} w="full">
                  <Text fontSize="sm" color={isDark ? 'gray.400' : 'gray.600'} fontWeight="medium">
                    Continue with
                  </Text>
                  <SimpleGrid columns={3} spacing={4} w="full">
                    <Button
                      leftIcon={<Icon as={FaGoogle} />}
                      variant="outline"
                      size="lg"
                      onClick={() => handleOAuthLogin('Google')}
                      borderColor={isDark ? 'gray.600' : 'gray.200'}
                      color={isDark ? 'gray.300' : 'gray.700'}
                      _hover={{ 
                        transform: 'translateY(-2px)',
                        shadow: 'lg',
                        borderColor: 'red.400',
                        bg: isDark ? 'gray.700' : 'gray.50'
                      }}
                      transition="all 0.2s"
                    >
                      Google
                    </Button>
                    <Button
                      leftIcon={<Icon as={FaApple} />}
                      variant="outline"
                      size="lg"
                      onClick={() => handleOAuthLogin('Apple')}
                      borderColor={isDark ? 'gray.600' : 'gray.200'}
                      color={isDark ? 'gray.300' : 'gray.700'}
                      _hover={{ 
                        transform: 'translateY(-2px)',
                        shadow: 'lg',
                        borderColor: 'gray.800',
                        bg: isDark ? 'gray.700' : 'gray.50'
                      }}
                      transition="all 0.2s"
                    >
                      Apple
                    </Button>
                    <Button
                      leftIcon={<Icon as={FaGithub} />}
                      variant="outline"
                      size="lg"
                      onClick={() => handleOAuthLogin('GitHub')}
                      borderColor={isDark ? 'gray.600' : 'gray.200'}
                      color={isDark ? 'gray.300' : 'gray.700'}
                      _hover={{ 
                        transform: 'translateY(-2px)',
                        shadow: 'lg',
                        borderColor: 'gray.800',
                        bg: isDark ? 'gray.700' : 'gray.50'
                      }}
                      transition="all 0.2s"
                    >
                      GitHub
                    </Button>
                  </SimpleGrid>
                </VStack>

                <HStack w="full">
                  <Divider borderColor={isDark ? 'gray.600' : 'gray.200'} />
                  <Text fontSize="sm" color={isDark ? 'gray.400' : 'gray.600'} px={4}>
                    or
                  </Text>
                  <Divider borderColor={isDark ? 'gray.600' : 'gray.200'} />
                </HStack>

                {/* Email/Password Login */}
                <Tabs 
                  index={activeTab} 
                  onChange={setActiveTab} 
                  w="full"
                  variant="soft-rounded"
                  colorScheme="blue"
                >
                  <TabList bg={isDark ? 'gray.700' : 'gray.50'} p={1} borderRadius="xl">
                    <Tab flex={1}>
                      <HStack spacing={2}>
                        <Icon as={FaLock} />
                        <Text>Password</Text>
                      </HStack>
                    </Tab>
                    <Tab flex={1}>
                      <HStack spacing={2}>
                        <Icon as={FaEnvelope} />
                        <Text>Email Code</Text>
                      </HStack>
                    </Tab>
                  </TabList>

                  <TabPanels>
                    {/* Password Tab */}
                    <TabPanel px={0}>
                      <form onSubmit={handlePasswordLogin}>
                        <VStack spacing={4}>
                          <FormControl isRequired>
                            <FormLabel color={isDark ? 'gray.300' : 'gray.800'}>Email</FormLabel>
                            <Input
                              type="email"
                              value={email}
                              onChange={(e) => setEmail(e.target.value)}
                              placeholder="Enter your email"
                              size="lg"
                              bg={isDark ? 'gray.700' : 'white'}
                              border="2px"
                              borderColor={isDark ? 'gray.600' : 'gray.200'}
                              color={isDark ? 'gray.300' : 'gray.800'}
                              _placeholder={{ color: isDark ? 'gray.500' : 'gray.400' }}
                              _hover={{ borderColor: 'blue.300' }}
                              _focus={{ borderColor: 'blue.500', boxShadow: 'none' }}
                            />
                          </FormControl>

                          <FormControl isRequired>
                            <FormLabel color={isDark ? 'gray.300' : 'gray.800'}>Password</FormLabel>
                            <InputGroup>
                              <Input
                                type={showPassword ? 'text' : 'password'}
                                value={password}
                                onChange={(e) => setPassword(e.target.value)}
                                placeholder="Enter your password"
                                size="lg"
                                bg={isDark ? 'gray.700' : 'white'}
                                border="2px"
                                borderColor={isDark ? 'gray.600' : 'gray.200'}
                                color={isDark ? 'gray.300' : 'gray.800'}
                                _placeholder={{ color: isDark ? 'gray.500' : 'gray.400' }}
                                _hover={{ borderColor: 'blue.300' }}
                                _focus={{ borderColor: 'blue.500', boxShadow: 'none' }}
                              />
                              <InputRightElement h="full">
                                <IconButton
                                  variant="ghost"
                                  onClick={() => setShowPassword(!showPassword)}
                                  icon={showPassword ? <ViewOffIcon /> : <ViewIcon />}
                                  aria-label="Toggle password visibility"
                                  color={isDark ? 'gray.400' : 'gray.600'}
                                />
                              </InputRightElement>
                            </InputGroup>
                          </FormControl>

                          <Button
                            type="submit"
                            size="lg"
                            w="full"
                            bgGradient="linear(to-r, blue.400, purple.500)"
                            color="white"
                            isLoading={isLoading}
                            loadingText="Signing in..."
                            _hover={{
                              bgGradient: 'linear(to-r, blue.500, purple.600)',
                              transform: 'translateY(-2px)',
                              shadow: 'lg'
                            }}
                            transition="all 0.2s"
                          >
                            Sign In
                          </Button>
                        </VStack>
                      </form>
                    </TabPanel>

                    {/* Email Code Tab */}
                    <TabPanel px={0}>
                      <VStack spacing={4}>
                        <FormControl isRequired>
                          <FormLabel color={isDark ? 'gray.300' : 'gray.800'}>Email</FormLabel>
                          <Input
                            type="email"
                            value={email}
                            onChange={(e) => setEmail(e.target.value)}
                            placeholder="Enter your email"
                            size="lg"
                            bg={isDark ? 'gray.700' : 'white'}
                            border="2px"
                            borderColor={isDark ? 'gray.600' : 'gray.200'}
                            color={isDark ? 'gray.300' : 'gray.800'}
                            _placeholder={{ color: isDark ? 'gray.500' : 'gray.400' }}
                            _hover={{ borderColor: 'blue.300' }}
                            _focus={{ borderColor: 'blue.500', boxShadow: 'none' }}
                          />
                        </FormControl>

                        {!isCodeSent ? (
                          <Button
                            onClick={handleSendCode}
                            size="lg"
                            w="full"
                            bgGradient="linear(to-r, green.400, blue.500)"
                            color="white"
                            isLoading={isLoading}
                            loadingText="Sending code..."
                            _hover={{
                              bgGradient: 'linear(to-r, green.500, blue.600)',
                              transform: 'translateY(-2px)',
                              shadow: 'lg'
                            }}
                            transition="all 0.2s"
                          >
                            Send Code
                          </Button>
                        ) : (
                          <VStack spacing={4} w="full">
                            <FormControl>
                              <FormLabel color={isDark ? 'gray.300' : 'gray.800'} textAlign="center">
                                Enter verification code
                              </FormLabel>
                              <Flex justify="center">
                                <PinInput
                                  value={code}
                                  onChange={setCode}
                                  size="lg"
                                  placeholder="0"
                                >
                                  <PinInputField 
                                    border="2px" 
                                    borderColor={isDark ? 'gray.600' : 'gray.200'}
                                    bg={isDark ? 'gray.700' : 'white'}
                                    color={isDark ? 'gray.300' : 'gray.800'}
                                    _hover={{ borderColor: 'blue.300' }}
                                    _focus={{ borderColor: 'blue.500' }}
                                  />
                                  <PinInputField 
                                    border="2px" 
                                    borderColor={isDark ? 'gray.600' : 'gray.200'}
                                    bg={isDark ? 'gray.700' : 'white'}
                                    color={isDark ? 'gray.300' : 'gray.800'}
                                    _hover={{ borderColor: 'blue.300' }}
                                    _focus={{ borderColor: 'blue.500' }}
                                  />
                                  <PinInputField 
                                    border="2px" 
                                    borderColor={isDark ? 'gray.600' : 'gray.200'}
                                    bg={isDark ? 'gray.700' : 'white'}
                                    color={isDark ? 'gray.300' : 'gray.800'}
                                    _hover={{ borderColor: 'blue.300' }}
                                    _focus={{ borderColor: 'blue.500' }}
                                  />
                                  <PinInputField 
                                    border="2px" 
                                    borderColor={isDark ? 'gray.600' : 'gray.200'}
                                    bg={isDark ? 'gray.700' : 'white'}
                                    color={isDark ? 'gray.300' : 'gray.800'}
                                    _hover={{ borderColor: 'blue.300' }}
                                    _focus={{ borderColor: 'blue.500' }}
                                  />
                                  <PinInputField 
                                    border="2px" 
                                    borderColor={isDark ? 'gray.600' : 'gray.200'}
                                    bg={isDark ? 'gray.700' : 'white'}
                                    color={isDark ? 'gray.300' : 'gray.800'}
                                    _hover={{ borderColor: 'blue.300' }}
                                    _focus={{ borderColor: 'blue.500' }}
                                  />
                                  <PinInputField 
                                    border="2px" 
                                    borderColor={isDark ? 'gray.600' : 'gray.200'}
                                    bg={isDark ? 'gray.700' : 'white'}
                                    color={isDark ? 'gray.300' : 'gray.800'}
                                    _hover={{ borderColor: 'blue.300' }}
                                    _focus={{ borderColor: 'blue.500' }}
                                  />
                                </PinInput>
                              </Flex>
                            </FormControl>

                            <Button
                              onClick={handleCodeLogin}
                              size="lg"
                              w="full"
                              bgGradient="linear(to-r, blue.400, purple.500)"
                              color="white"
                              isLoading={isLoading}
                              loadingText="Verifying..."
                              _hover={{
                                bgGradient: 'linear(to-r, blue.500, purple.600)',
                                transform: 'translateY(-2px)',
                                shadow: 'lg'
                              }}
                              transition="all 0.2s"
                            >
                              Verify & Sign In
                            </Button>

                            <Button
                              onClick={() => setIsCodeSent(false)}
                              variant="ghost"
                              size="sm"
                              color={isDark ? 'gray.400' : 'gray.600'}
                            >
                              Back to email
                            </Button>
                          </VStack>
                        )}
                      </VStack>
                    </TabPanel>
                  </TabPanels>
                </Tabs>

                <Divider borderColor={isDark ? 'gray.600' : 'gray.200'} />

                {/* Footer Links */}
                <VStack spacing={3} w="full">
                  <HStack spacing={1}>
                    <Text color={isDark ? 'gray.400' : 'gray.600'} fontSize="sm">
                      Don't have an account?
                    </Text>
                    <Link 
                      as={RouterLink} 
                      to="/register" 
                      color="blue.500"
                      fontWeight="medium"
                      _hover={{ color: 'blue.400' }}
                    >
                      Sign up
                    </Link>
                  </HStack>
                  
                  <Link 
                    as={RouterLink} 
                    to="/password-reset" 
                    color="blue.500"
                    fontSize="sm"
                    _hover={{ color: 'blue.400' }}
                  >
                    Forgot your password?
                  </Link>
                </VStack>
              </VStack>
            </CardBody>
          </Card>
        </VStack>
      </Container>
    </Box>
  );
}; 